import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { Button } from "react-bootstrap";

import { addCartItemRequest, cartItemIds, addPreview } from "../actions/index";

// Hit component styled like Main.js but for upsells
const UpsellHit = connect(state => ({
  cart_items_ids: cartItemIds(state.cart_items)
}))(({ hit, dispatch, cart_items_ids, ownedProductIds }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isMountedRef = React.useRef(true);
  const productId = hit.id;
  const isInCart = cart_items_ids.includes(productId);
  const isOwned = ownedProductIds.includes(productId);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMountedRef.current) return;
    setIsAdding(true);
    try {
      await dispatch(addCartItemRequest(productId));
    } catch (error) {
      console.error("Failed to add item to cart:", error);
    } finally {
      if (isMountedRef.current) {
        setIsAdding(false);
      }
    }
  };

  const handlePlayPreview = () => {
    if (!hit.audio_preview_url) {
      return;
    }

    const previewData = {
      id: productId,
      name: hit.name,
      preview: hit.audio_preview_url,
      image: hit.images?.mid,
      manufacturer_name: hit.manufacturer_name || 'Unknown'
    };

    dispatch(addPreview(previewData));
  };

  const handleCardClick = (e) => {
    // Only navigate if not clicking on interactive elements
    if (e.target.closest('.pack-cart-button') || e.target.closest('.preset-pack-image') || e.target.closest('.audio-indicator')) {
      return;
    }
    e.preventDefault();
    window.location.href = `/preset_packs/${hit.slug}`;
  };

  const handleImageClick = (e) => {
    // Don't handle if clicking on the audio indicator
    if (e.target.closest('.audio-indicator')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (hit.audio_preview_url) {
      // Use setTimeout to prevent Redux dispatch from interfering
      setTimeout(() => {
        if (isMountedRef.current) {
          handlePlayPreview();
        }
      }, 0);
    } else {
      window.location.href = `/preset_packs/${hit.slug}`;
    }
  };

  const handleAudioIndicatorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Use setTimeout to prevent Redux dispatch from interfering
    setTimeout(() => {
      if (isMountedRef.current) {
        handlePlayPreview();
      }
    }, 0);
  };

  return (
    <div className="preset-pack-card upsell-card" data-card="true" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div
        className={`preset-pack-image ${hit.audio_preview_url ? 'clickable-audio' : 'clickable-nav'}`}
        onClick={handleImageClick}
        title={hit.audio_preview_url ? 'Click to play preview' : 'Click to view details'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {hit.images?.full && (
          <div className="image-container">
            <img
              src={hit.images.full}
              alt={hit.name}
              className="pack-artwork"
            />
          </div>
        )}
        {hit.audio_preview_url && isHovered && (
          <div className="audio-indicator" onClick={handleAudioIndicatorClick}>
            <i className="fa fa-play"></i>
          </div>
        )}
        {/* Special badges for exclusive, featured, new - overlaid on image */}
        {(hit.exclusive || hit.featured || hit.is_new) && (
          <div className="pack-badges-overlay">
            {hit.exclusive && <span className="badge badge-exclusive">Exclusive</span>}
            {hit.featured && <span className="badge badge-featured">Featured</span>}
            {hit.is_new && <span className="badge badge-new">New</span>}
          </div>
        )}
      </div>
      <div className="preset-pack-content">
        <div className="pack-header">
          <h6 className="pack-title mb-1">{hit.name}</h6>
          <div className="pack-meta">
            {hit.manufacturer_name && (
              <small className="pack-manufacturer text-muted">by {hit.manufacturer_name}</small>
            )}
          </div>
        </div>

        {/* Genre list */}
        {hit.genres && hit.genres.length > 0 && (
          <div className="genre-list">
            <small className="text-muted">
              {hit.genres.slice(0, 2).map(genre => genre.name).join(', ')}
              {hit.genres.length > 2 && (
                <span className="genre-more"> +{hit.genres.length - 2}</span>
              )}
            </small>
          </div>
        )}

        {/* Serum sections */}
        {(hit.serum_1_presets || hit.serum_2_presets) && (
          <div className="preset-info">
            <small className="text-muted">
              {hit.serum_1_presets && (
                <span className="preset-count">{hit.serum_1_presets} Serum 1</span>
              )}
              {hit.serum_2_presets && (
                <span className="preset-count"> {hit.serum_2_presets} Serum 2</span>
              )}
            </small>
          </div>
        )}

        {/* Cart button */}
        <div className="pack-actions mt-2">
          {isAdding ? (
            <Button size="sm" className="pack-cart-button w-100" variant="secondary" disabled>
              <i className="fa fa-spinner fa-spin fa-xs"></i>
            </Button>
          ) : (isOwned || isInCart) ? (
            <Button size="sm" className="pack-cart-button w-100" variant="success" disabled>
              <i className="fa fa-check fa-xs"></i>
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              size="sm"
              className="pack-cart-button w-100"
              variant="primary"
            >
              <small>{hit.price || '$0.00'}</small>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

const UpsellPresets = ({ cart_items, ownedProductIds = [] }) => {
  const [upsellPacks, setUpsellPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpsells = async () => {
      try {
        setLoading(true);

        // Get IDs from cart items and owned products to exclude
        const cartProductIds = cart_items.map(item => item.id);
        const excludedIds = [...cartProductIds, ...ownedProductIds];

        // Build query parameters
        const params = new URLSearchParams();
        if (excludedIds.length > 0) {
          params.append('exclude_ids', excludedIds.join(','));
        }

        // Fetch upsell presets from the new endpoint
        const response = await fetch(`/shopping_cart/upsell_presets?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setUpsellPacks(data.preset_packs || []);

      } catch (error) {
        console.error('Error fetching upsells:', error);
        setUpsellPacks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUpsells();
  }, [cart_items, ownedProductIds]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <i className="fa fa-spinner fa-spin fa-lg"></i>
        <p className="mt-2 small">Loading recommendations...</p>
      </div>
    );
  }

  if (upsellPacks.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="mt-3 mb-2">
        <h5 className="mb-1">You Might Also Like</h5>
        <p className="text-muted small mb-0">Latest preset packs you might enjoy</p>
      </div>

      <Row className="upsell-presets">
        {upsellPacks.map((pack) => (
          <Col lg={3} md={4} sm={6} key={pack.id} className="mb-2">
            <UpsellHit hit={pack} ownedProductIds={ownedProductIds} />
          </Col>
        ))}
      </Row>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  cart_items: state.cart_items
});

export default connect(mapStateToProps)(UpsellPresets);